import {ReactNode, isValidElement} from "react";
import {
    RenderableDocument,
    DibJsonNode,
    ElementDataTags,
    RenderNodeOptions,
} from "../../types";
import {Stikkord} from "./Stikkord";

const getAccountingTableIndex = (renderedChildren: ReactNode[]) => {
    return renderedChildren.findIndex((c) => {
        if (!isValidElement(c)) return false;
        return (c.props as Record<string, unknown>).augmentation === "AccountingTable";
    });
};

const getStrekIndex = (renderedChildren: ReactNode[]) => {
    return renderedChildren.findIndex((child) => {
        if (!isValidElement(child)) return false;
        const className = (child.props as Record<string, unknown>).className;
        return typeof className === "string" && className.includes("strek");
    });
};

const getBookTagsIndex = (renderedChildren: ReactNode[]) => {
    return renderedChildren.findIndex((child) => {
        if (!isValidElement(child)) return false;
        return (
            typeof child.type === "function" &&
            (child.type as {name?: string}).name === "BookTags"
        );
    });
};

const getLastSpecialItemIndex = (renderedChildren: ReactNode[]) => {
    return renderedChildren.reduce<number>((accumulator, current, index) => {
        if (!isValidElement(current)) return accumulator;

        if (
            current.type === "p" ||
            current.type === "ul" ||
            (typeof current.type === "function" &&
                (current.props as Record<string, unknown>).node &&
                ((current.props as Record<string, unknown>).node as DibJsonNode)
                    ?.kind === "xbox")
        ) {
            return index;
        }
        return accumulator;
    }, -1);
};

const insertAfter = (
    renderedChildren: ReactNode[],
    index: number,
    element: ReactNode
) => [
    ...renderedChildren.slice(0, index + 1),
    element,
    ...renderedChildren.slice(index + 1),
];

const test = (node: DibJsonNode, _options: RenderNodeOptions) => {
    const elementData = node.elementData as ElementDataTags | null;
    return !!elementData?.tags;
};

const processor = (
    node: DibJsonNode,
    topicData: RenderableDocument,
    renderedChildren: ReactNode[]
) => {
    const stikkordElement = (
        <Stikkord
            key={`${node.element?.attributes?.id}-stikkord`}
            augmentation="Stikkord"
            node={node}
            topicData={topicData}
        />
    );

    // Placement priority:
    // 1. After AccountingTable if present
    // 2. After "strek" element if present
    // 3. After BookTags if present
    // 4. After last p, ul, or xbox element
    // 5. After first child (typically a heading)

    const accTableIndex = getAccountingTableIndex(renderedChildren);
    if (accTableIndex !== -1) {
        return insertAfter(renderedChildren, accTableIndex, stikkordElement);
    }

    const strekIndex = getStrekIndex(renderedChildren);
    if (strekIndex !== -1) {
        return insertAfter(renderedChildren, strekIndex, stikkordElement);
    }

    const bookTagsIndex = getBookTagsIndex(renderedChildren);
    if (bookTagsIndex !== -1) {
        return insertAfter(renderedChildren, bookTagsIndex, stikkordElement);
    }

    const specialItemIndex = getLastSpecialItemIndex(renderedChildren);
    if (specialItemIndex !== -1) {
        return insertAfter(renderedChildren, specialItemIndex, stikkordElement);
    }

    return [renderedChildren[0], stikkordElement, ...renderedChildren.slice(1)];
};

export const stikkord = {test, processor};
